/**
 * Мини-чат поверх игры: обсудить краш, не уходя с экрана.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";

import {
  CHAT_REACTIONS,
  CHAT_SLOW_OPTIONS,
  CHAT_STICKERS,
  chatStickerById,
  formatMuteRemain,
  type ChatMessageDto,
  type ChatMuteDto,
  type ChatMuteDuration,
} from "@koshkodrop/shared";

import { PawRain } from "../chat/PawRain";
import { formatChatTime, nameColor } from "../chat/colors";
import { haptic } from "../lib/telegram";
import { useChat } from "../store/chat";
import { useSession } from "../store/session";

interface Props {
  onClose: () => void;
}

const MUTE_OPTIONS: { id: ChatMuteDuration; label: string }[] = [
  { id: "15m", label: "15 мин" },
  { id: "1h", label: "1 час" },
  { id: "12h", label: "12 ч" },
  { id: "forever", label: "Навсегда" },
];

export function MiniChat({ onClose }: Props) {
  const user = useSession((state) => state.user);
  const {
    connection,
    room,
    messages,
    online,
    me,
    mutes,
    typing,
    fx,
    lastError,
    replyTo,
    send,
    react,
    typingNow,
    remove,
    mute,
    unmute,
    setSlow,
    setReply,
    clearError,
    clearFx,
  } = useChat();

  const [draft, setDraft] = useState("");
  const [stickersOpen, setStickersOpen] = useState(false);
  const [gearOpen, setGearOpen] = useState(false);
  const [picked, setPicked] = useState<ChatMessageDto | null>(null);
  const bottom = useRef<HTMLDivElement | null>(null);
  const now = useNow(Boolean(me?.mutedForever || me?.mutedUntil || mutes.length > 0));

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, typing, stickersOpen]);

  useEffect(() => {
    if (!lastError) return;
    const timer = window.setTimeout(clearError, 2800);
    return () => window.clearTimeout(timer);
  }, [lastError, clearError]);

  useEffect(() => {
    if (!typing) return;
    const wait = typing.until - Date.now();
    if (wait <= 0) return;
    const timer = window.setTimeout(() => {
      useChat.setState((state) =>
        state.typing?.until && state.typing.until <= Date.now() ? { typing: null } : state,
      );
    }, wait + 30);
    return () => window.clearTimeout(timer);
  }, [typing]);

  const muted = Boolean(
    me?.mutedForever || (me?.mutedUntil && Date.parse(me.mutedUntil) > now),
  );
  const myId = user?.id;

  const submit = () => {
    const body = draft.trim();
    if (!body || muted) return;
    send("text", body);
    setDraft("");
    setStickersOpen(false);
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-t-3xl bg-night-900/98">
      <PawRain kind={fx?.kind ?? null} nonce={fx?.nonce ?? null} onDone={clearFx} />

      <header className="relative z-10 border-b border-white/5 px-3 pb-2 pt-2">
        <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-white/15" />
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-extrabold text-white">Чат стола</p>
            <p className="text-[11px] text-white/40">
              {connection === "open"
                ? `${Math.max(online.length, 1)} онлайн`
                : connection === "connecting"
                  ? "Подключаюсь…"
                  : "Переподключаюсь…"}
            </p>
          </div>
          <OnlineStack people={online} />
          {me?.canModerate && (
            <button
              type="button"
              onClick={() => {
                haptic.select();
                setGearOpen((open) => !open);
              }}
              className="rounded-xl bg-night-850 px-2.5 py-2 text-sm"
            >
              ⚙️
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              haptic.select();
              onClose();
            }}
            className="rounded-xl bg-night-850 px-2.5 py-2 text-sm font-bold text-white/60"
          >
            ✕
          </button>
        </div>

        {gearOpen && me?.canModerate && (
          <div className="mt-2 rounded-2xl border border-white/8 bg-night-850/90 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wide text-white/35">
              Медленный режим
            </p>
            <div className="mt-2 flex gap-1.5">
              {CHAT_SLOW_OPTIONS.map((seconds) => {
                const active = (room?.slowModeSeconds ?? 0) === seconds;
                return (
                  <button
                    key={seconds}
                    type="button"
                    onClick={() => {
                      haptic.tap("medium");
                      setSlow(seconds);
                    }}
                    className={`flex-1 rounded-xl py-2 text-[11px] font-extrabold ${
                      active ? "bg-paws-500 text-night-950" : "bg-night-900 text-white/55"
                    }`}
                  >
                    {seconds === 0 ? "выкл" : `${seconds}с`}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {me?.canModerate && (
          <MuteList mutes={mutes} now={now} onUnmute={unmute} />
        )}
      </header>

      <div className="relative z-10 min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-1 px-6 text-center">
            <span className="text-3xl">😺</span>
            <p className="text-sm font-extrabold text-white">Пока тихо</p>
            <p className="text-[11px] leading-relaxed text-white/45">
              Напиши, не уходя со стола — история живёт 12 часов.
            </p>
          </div>
        )}

        {messages.map((message, index) => {
          const previous = messages[index - 1];
          const stacked =
            previous?.author.userId === message.author.userId &&
            Date.parse(message.createdAt) - Date.parse(previous.createdAt) < 120_000;
          return (
            <Bubble
              key={message.id}
              message={message}
              mine={message.author.userId === myId}
              stacked={stacked}
              onPick={() => {
                haptic.select();
                setPicked(message);
              }}
              onReact={(emoji) => react(message.id, emoji)}
            />
          );
        })}

        {typing && typing.until > Date.now() && (
          <p className="mt-1 px-2 text-[11px] text-white/35">{typing.firstName} печатает…</p>
        )}
        <div ref={bottom} />
      </div>

      {lastError && (
        <p className="relative z-10 mx-3 mb-2 rounded-2xl bg-lose-500/15 px-3 py-2 text-center text-xs font-semibold text-lose-300">
          {lastError}
        </p>
      )}

      {muted && (
        <div className="relative z-10 mx-3 mb-2 rounded-2xl border border-lose-500/25 bg-lose-500/10 px-3 py-2">
          <p className="text-center text-xs font-extrabold text-lose-300">
            {me?.mutedForever
              ? "Мут навсегда"
              : `Мут · осталось ${formatMuteRemain(me?.mutedUntil ?? null, false, now)}`}
          </p>
        </div>
      )}

      {replyTo && (
        <div className="relative z-10 mx-3 mb-2 flex items-center gap-2 rounded-2xl border border-white/8 bg-night-850 px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold text-paws-300">Ответ {replyTo.author.firstName}</p>
            <p className="truncate text-xs text-white/50">{previewOf(replyTo)}</p>
          </div>
          <button type="button" onClick={() => setReply(null)} className="text-white/40">
            ✕
          </button>
        </div>
      )}

      {stickersOpen && !muted && (
        <div className="relative z-10 mx-3 mb-2 grid grid-cols-6 gap-1.5 rounded-3xl border border-white/8 bg-night-850/95 p-2.5">
          {CHAT_STICKERS.map((sticker) => (
            <button
              key={sticker.id}
              type="button"
              onClick={() => {
                send("sticker", sticker.id);
                setStickersOpen(false);
              }}
              className="flex flex-col items-center rounded-2xl py-1.5 active:scale-95"
            >
              <span className="text-2xl">{sticker.emoji}</span>
            </button>
          ))}
        </div>
      )}

      <form
        className="relative z-10 border-t border-white/5 bg-night-900 px-3 pb-2 pt-2"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="flex items-end gap-2">
          <button
            type="button"
            disabled={muted}
            onClick={() => {
              haptic.select();
              setStickersOpen((open) => !open);
            }}
            className="mb-0.5 rounded-2xl bg-night-850 px-3 py-3 text-lg disabled:opacity-30"
          >
            🐾
          </button>
          <input
            value={draft}
            disabled={muted}
            onChange={(event) => {
              setDraft(event.target.value);
              typingNow();
            }}
            maxLength={280}
            placeholder={muted ? "сообщения ограничены" : "Написать котикам…"}
            className="min-h-12 flex-1 rounded-2xl bg-night-850 px-3.5 py-3 text-sm text-white outline-none placeholder:text-white/25 disabled:opacity-40"
          />
          <button
            type="submit"
            disabled={muted || !draft.trim()}
            className="mb-0.5 rounded-2xl bg-paws-500 px-4 py-3 text-sm font-extrabold text-night-950 disabled:opacity-30"
          >
            Мур
          </button>
        </div>
      </form>

      {picked && (
        <ActionSheet
          message={picked}
          mine={picked.author.userId === myId}
          canModerate={Boolean(me?.canModerate)}
          existingMute={mutes.find((item) => item.userId === picked.author.userId) ?? null}
          now={now}
          onClose={() => setPicked(null)}
          onReply={() => {
            setReply(picked);
            setPicked(null);
          }}
          onReact={(emoji) => {
            react(picked.id, emoji);
            setPicked(null);
          }}
          onDelete={() => {
            remove(picked.id);
            setPicked(null);
          }}
          onMute={(duration) => {
            mute(picked.author.userId, duration);
            setPicked(null);
          }}
          onUnmute={() => {
            unmute(picked.author.userId);
            setPicked(null);
          }}
        />
      )}
    </div>
  );
}

function previewOf(message: ChatMessageDto): string {
  if (message.kind === "sticker") {
    return chatStickerById(message.body)?.emoji ?? "🐾";
  }
  return message.body;
}

function useNow(active: boolean): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

function MuteList({
  mutes,
  now,
  onUnmute,
}: {
  mutes: ChatMuteDto[];
  now: number;
  onUnmute: (userId: string) => void;
}) {
  const live = mutes.filter((mute) => mute.forever || (mute.until && Date.parse(mute.until) > now));
  if (live.length === 0) return null;
  return (
    <div className="mt-2">
      <p className="text-[10px] font-bold uppercase tracking-wide text-white/35">Ограничения</p>
      <ul className="mt-1.5 max-h-24 space-y-1.5 overflow-y-auto">
        {live.map((mute) => (
          <li
            key={mute.userId}
            className="flex items-center gap-2 rounded-xl bg-night-900 px-2.5 py-2"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-white">{mute.firstName}</p>
              <p className="tabular text-[11px] font-extrabold text-lose-300">
                {formatMuteRemain(mute.until, mute.forever, now)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                haptic.tap("medium");
                onUnmute(mute.userId);
              }}
              className="rounded-lg bg-paws-500 px-2.5 py-1 text-[11px] font-extrabold text-night-950"
            >
              Снять
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OnlineStack({
  people,
}: {
  people: { userId: string; firstName: string; photoUrl: string | null }[];
}) {
  const shown = people.slice(0, 4);
  if (shown.length === 0) return null;
  return (
    <div className="flex -space-x-2">
      {shown.map((person) =>
        person.photoUrl ? (
          <img
            key={person.userId}
            src={person.photoUrl}
            alt=""
            className="size-7 rounded-full border-2 border-night-950 object-cover"
          />
        ) : (
          <span
            key={person.userId}
            className="flex size-7 items-center justify-center rounded-full border-2 border-night-950 bg-night-700 text-[11px]"
          >
            {person.firstName.slice(0, 1)}
          </span>
        ),
      )}
    </div>
  );
}

function Bubble({
  message,
  mine,
  stacked,
  onPick,
  onReact,
}: {
  message: ChatMessageDto;
  mine: boolean;
  stacked: boolean;
  onPick: () => void;
  onReact: (emoji: string) => void;
}) {
  const color = message.author.nickColor || nameColor(message.author.userId);
  const sticker = message.kind === "sticker" ? chatStickerById(message.body) : undefined;
  const hold = useRef<number | null>(null);

  const startHold = () => {
    hold.current = window.setTimeout(onPick, 380);
  };
  const cancelHold = () => {
    if (hold.current !== null) {
      window.clearTimeout(hold.current);
      hold.current = null;
    }
  };

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} ${stacked ? "mt-1" : "mt-3"}`}>
      <div className={`max-w-[82%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
        {!mine && !stacked && (
          <div className="mb-1 flex items-center gap-1.5 px-1">
            {message.author.photoUrl ? (
              <img src={message.author.photoUrl} alt="" className="size-5 rounded-full object-cover" />
            ) : (
              <span className="flex size-5 items-center justify-center rounded-full bg-night-700 text-[10px]">
                {message.author.firstName.slice(0, 1)}
              </span>
            )}
            <span className="text-[11px] font-extrabold" style={{ color }}>
              {message.author.firstName}
            </span>
            {message.author.nickSticker ? (
              <span className="text-[11px]">{message.author.nickSticker}</span>
            ) : message.author.role === "admin" ? (
              <span className="text-[10px]">👑</span>
            ) : null}
          </div>
        )}

        <button
          type="button"
          onContextMenu={(event) => {
            event.preventDefault();
            onPick();
          }}
          onTouchStart={startHold}
          onTouchEnd={cancelHold}
          onTouchMove={cancelHold}
          onClick={() => haptic.select()}
          className={`rounded-3xl px-3.5 py-2.5 text-left ${
            mine
              ? "rounded-br-md bg-paws-500/20 text-white"
              : "rounded-bl-md bg-night-850 text-white"
          }`}
        >
          {message.replyTo && (
            <p className="mb-1.5 border-l-2 border-paws-400/70 pl-2 text-[11px] text-white/45">
              <span className="font-bold text-white/60">{message.replyTo.firstName}</span>{" "}
              {message.replyTo.preview}
            </p>
          )}
          {sticker ? (
            <span className="block text-[32px] leading-none">{sticker.emoji}</span>
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm leading-snug">{message.body}</p>
          )}
          <p className={`mt-1 text-[9px] ${mine ? "text-white/35" : "text-white/25"}`}>
            {formatChatTime(message.createdAt)}
          </p>
        </button>

        {message.reactions.length > 0 && (
          <div className={`mt-1 flex flex-wrap gap-1 ${mine ? "justify-end" : "justify-start"}`}>
            {message.reactions.map((reaction) => (
              <button
                key={reaction.emoji}
                type="button"
                onClick={() => onReact(reaction.emoji)}
                className={`rounded-full px-1.5 py-0.5 text-[11px] ${
                  reaction.me ? "bg-paws-500/25 text-paws-300" : "bg-night-850 text-white/55"
                }`}
              >
                {reaction.emoji} {reaction.count}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionSheet({
  message,
  mine,
  canModerate,
  existingMute,
  now,
  onClose,
  onReply,
  onReact,
  onDelete,
  onMute,
  onUnmute,
}: {
  message: ChatMessageDto;
  mine: boolean;
  canModerate: boolean;
  existingMute: ChatMuteDto | null;
  now: number;
  onClose: () => void;
  onReply: () => void;
  onReact: (emoji: string) => void;
  onDelete: () => void;
  onMute: (duration: ChatMuteDuration) => void;
  onUnmute: () => void;
}) {
  return (
    <Sheet onClose={onClose}>
      <p className="mb-3 truncate text-sm font-bold text-white/50">{message.author.firstName}</p>
      <div className="mb-3 flex justify-between gap-2">
        {CHAT_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onReact(emoji)}
            className="flex-1 rounded-2xl bg-night-800 py-2.5 text-xl active:scale-95"
          >
            {emoji}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onReply}
        className="w-full rounded-2xl bg-night-800 py-3 text-sm font-bold text-white"
      >
        Ответить
      </button>
      {canModerate && (
        <>
          <button
            type="button"
            onClick={onDelete}
            className="mt-2 w-full rounded-2xl bg-lose-500/15 py-3 text-sm font-bold text-lose-300"
          >
            Удалить сообщение
          </button>
          {!mine && existingMute && (
            <div className="mt-3 rounded-2xl border border-lose-500/20 bg-lose-500/10 p-3">
              <p className="text-center text-xs font-extrabold text-lose-300">
                Мут · {formatMuteRemain(existingMute.until, existingMute.forever, now)}
              </p>
              <button
                type="button"
                onClick={onUnmute}
                className="mt-2 w-full rounded-xl bg-paws-500 py-2.5 text-sm font-extrabold text-night-950"
              >
                Снять ограничение
              </button>
            </div>
          )}
          {!mine && !existingMute && (
            <div className="mt-3">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-white/35">
                Ограничить сообщения
              </p>
              <div className="grid grid-cols-2 gap-2">
                {MUTE_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => onMute(option.id)}
                    className="rounded-2xl bg-night-800 py-2.5 text-xs font-bold text-white/70"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}

function Sheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className="absolute inset-0 z-40 flex items-end bg-black/50" onClick={onClose}>
      <div
        className="safe-bottom w-full rounded-t-3xl border-t border-white/10 bg-night-900 px-4 pb-4 pt-4"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15" />
        {children}
      </div>
    </div>
  );
}
